import React, { useState } from 'react';

import { ICPlusSection, SurchargeSection, TieredSection } from './components';

import type {
	EnhancedCustomRatesResponse,
	PricingConfiguration,
	UnifiedRateConfig,
} from '../../../types';

interface DiscountRateStepProps {
	configuration: PricingConfiguration;
	onUpdate: (updates: Partial<PricingConfiguration>) => void;
	onNext: () => void;
	onPrevious: () => void;
	hideNavigation?: boolean;
	enhancedApiResponse?: EnhancedCustomRatesResponse;
}

export const DiscountRateStep: React.FC<DiscountRateStepProps> = ({
	configuration,
	onUpdate,
	onNext,
	onPrevious,
	hideNavigation = false,
	enhancedApiResponse,
}) => {
	// Helper function to check if a rate type is selected
	const hasRateType = (rateType: string) => {
		const selectedTypes = configuration.selected_rate_types || [
			configuration.rate_plan_type,
		];
		return selectedTypes?.includes(rateType as any) || false;
	};
	const [sameAsAbove, setSameAsAbove] = useState(true);

	React.useEffect(() => {
		if (enhancedApiResponse?.rates?.tiered_rates?.same_for_all) {
			const sameAsCredit =
				enhancedApiResponse.rates.tiered_rates.same_for_all.same_as_credit;
			setSameAsAbove(sameAsCredit);
		} else if (configuration.tiered_same_as_credit_6tier !== undefined) {
			setSameAsAbove(configuration.tiered_same_as_credit_6tier);
		} else if (configuration.same_as_credit_6tier !== undefined) {
			setSameAsAbove(configuration.same_as_credit_6tier);
		}
	}, [
		enhancedApiResponse,
		configuration.tiered_same_as_credit_6tier,
		configuration.same_as_credit_6tier,
	]);

	const threeTierRates = React.useMemo(() => {
		return {
			visa_mastercard_discover_credit: configuration.credit_rates || {
				qualified_rate: 1.65,
				qualified_per_item: 0.91,
				midqualified_rate: 1.75,
				midqualified_per_item: 0.91,
				nonqualified_rate: 2.25,
				nonqualified_per_item: 0.91,
			},
			american_express: configuration.amex_rates || {
				qualified_rate: 1.85,
				qualified_per_item: 0.1,
				midqualified_rate: 2.5,
				midqualified_per_item: 0.1,
				nonqualified_rate: 2.85,
				nonqualified_per_item: 0.1,
			},
		};
	}, [
		configuration.credit_rates?.qualified_rate,
		configuration.credit_rates?.qualified_per_item,
		configuration.credit_rates?.midqualified_rate,
		configuration.credit_rates?.midqualified_per_item,
		configuration.credit_rates?.nonqualified_rate,
		configuration.credit_rates?.nonqualified_per_item,
		configuration.amex_rates?.qualified_rate,
		configuration.amex_rates?.qualified_per_item,
		configuration.amex_rates?.midqualified_rate,
		configuration.amex_rates?.midqualified_per_item,
		configuration.amex_rates?.nonqualified_rate,
		configuration.amex_rates?.nonqualified_per_item,
	]);

	const sixTierRates = React.useMemo(() => {
		return {
			visa_mastercard_discover_credit: configuration.debit_rates || {
				qualified_rate: 1.6,
				qualified_per_item: 0.79,
				midqualified_rate: 1.7,
				midqualified_per_item: 0.79,
				nonqualified_rate: 2.2,
				nonqualified_per_item: 0.79,
			},
			visa_mastercard_discover_debit: configuration.visa_debit_rates || {
				qualified_rate: 0.55,
				qualified_per_item: 0.15,
				midqualified_rate: 1.65,
				midqualified_per_item: 0.15,
				nonqualified_rate: 2.1,
				nonqualified_per_item: 0.15,
			},
			pin_debit: configuration.pin_debit_rates || {
				qualified_rate: 1.85,
				qualified_per_item: 0.1,
				midqualified_rate: 2.5,
				midqualified_per_item: 0.1,
				nonqualified_rate: 2.85,
				nonqualified_per_item: 0.1,
			},
		};
	}, [
		configuration.debit_rates?.qualified_rate,
		configuration.debit_rates?.qualified_per_item,
		configuration.debit_rates?.midqualified_rate,
		configuration.debit_rates?.midqualified_per_item,
		configuration.debit_rates?.nonqualified_rate,
		configuration.debit_rates?.nonqualified_per_item,
		configuration.visa_debit_rates?.qualified_rate,
		configuration.visa_debit_rates?.qualified_per_item,
		configuration.visa_debit_rates?.midqualified_rate,
		configuration.visa_debit_rates?.midqualified_per_item,
		configuration.visa_debit_rates?.nonqualified_rate,
		configuration.visa_debit_rates?.nonqualified_per_item,
		configuration.pin_debit_rates?.qualified_rate,
		configuration.pin_debit_rates?.qualified_per_item,
		configuration.pin_debit_rates?.midqualified_rate,
		configuration.pin_debit_rates?.midqualified_per_item,
		configuration.pin_debit_rates?.nonqualified_rate,
		configuration.pin_debit_rates?.nonqualified_per_item,
	]);

	// Global discount rate mode change (legacy, only used when single rate type)
	const handleDiscountRateChange = (
		mode: 'same_for_all' | 'different_by_card_type',
	) => {
		onUpdate({ discount_rate_mode: mode });
	};

	// UNIFIED: Mode change handler that updates rate-type-specific fields
	// This is called by ICPlusSection for both ic_plus and flat_tiered rate types
	const handleDiscountRateModeChange = (
		mode: 'same_for_all' | 'different_by_card_type',
		rateType?: 'ic_plus' | 'flat_tiered',
	) => {
		const updates: Partial<PricingConfiguration> = {};

		// Determine the active rate type based on what's selected
		const activeRateType =
			rateType || (hasRateType('FLAT_TIERED') ? 'flat_tiered' : 'ic_plus');

		// Get current standard rates for this rate type
		const getStandardRate = () => {
			if (activeRateType === 'flat_tiered') {
				return configuration.flat_tiered_standard_discount_rate ?? 2.9;
			}
			return configuration.ic_plus_standard_discount_rate ?? 0.65;
		};

		// Update the mode field for the specific rate type
		if (activeRateType === 'flat_tiered') {
			updates.flat_tiered_discount_rate_mode = mode;
		} else {
			updates.ic_plus_discount_rate_mode = mode;
		}

		// Also update the global mode for compatibility
		updates.discount_rate_mode = mode;

		// When switching to different_by_card_type, initialize card rates from standard rate
		if (mode === 'different_by_card_type') {
			const standardRate = getStandardRate();
			const cardRates = {
				visa_mastercard_discover_credit: standardRate,
				visa_mastercard_discover_debit: standardRate,
				american_express: standardRate,
				pin_debit: standardRate,
			};

			if (activeRateType === 'flat_tiered') {
				updates.flat_tiered_card_specific_discount_rates = cardRates;
			} else {
				updates.ic_plus_card_specific_discount_rates = cardRates;
			}
		}

		// Also update rate_config for backward compatibility with transformer
		const currentRateConfig = configuration.rate_config || {
			mode: 'same_for_all',
			standardRate: activeRateType === 'flat_tiered' ? 2.9 : 0.65,
			standardAuthFee: activeRateType === 'flat_tiered' ? 0.3 : 0.1,
			visa_mastercard_discover_credit: { discountRate: 2.9, authFee: 0.3 },
			visa_mastercard_discover_debit: { discountRate: 2.9, authFee: 0.3 },
			american_express: { discountRate: 2.9, authFee: 0.3 },
			pin_debit: { discountRate: 2.9, authFee: 0.3 },
		};

		const standardRate = getStandardRate();
		updates.rate_config = {
			...currentRateConfig,
			mode,
			...(mode === 'different_by_card_type'
				? {
						visa_mastercard_discover_credit: {
							...currentRateConfig.visa_mastercard_discover_credit,
							discountRate: standardRate,
						},
						visa_mastercard_discover_debit: {
							...currentRateConfig.visa_mastercard_discover_debit,
							discountRate: standardRate,
						},
						american_express: {
							...currentRateConfig.american_express,
							discountRate: standardRate,
						},
						pin_debit: {
							...currentRateConfig.pin_debit,
							discountRate: standardRate,
						},
					}
				: {}),
		};

		onUpdate(updates);
	};

	// TIERED: Mode change handler for tiered rates
	// Updates both tiered_discount_rate_mode (rate-type-specific) and discount_rate_mode (global fallback)
	const handleTieredDiscountRateModeChange = (
		mode: 'same_for_all' | 'different_by_card_type',
	) => {
		const updates: Partial<PricingConfiguration> = {
			tiered_discount_rate_mode: mode,
			discount_rate_mode: mode, // Also update global for backward compatibility
		};
		onUpdate(updates);
	};

	// TIERED: Card-specific rate update handler (for tiered rates structure)
	const updateTieredCardSpecificDiscountRate = (
		cardType: string,
		value: number,
	) => {
		// Tiered rates use a different structure (credit_rates, debit_rates, amex_rates)
		// This is handled inline in TieredSection component
		// This function is optional and can be a no-op or handle specific cases
	};

	// TIERED: Transaction-specific fee update handler
	const updateTieredTransactionSpecificFee = (
		transactionType: string,
		value: number,
	) => {
		// Tiered rates use qualified_per_item, midqualified_per_item, etc.
		// This is handled inline in TieredSection component
		// This function is optional and can be a no-op or handle specific cases
	};

	const updateNestedProperty = <T extends Record<string, any>>(
		currentObject: T | undefined,
		key: keyof T,
		value: any,
		defaultObject: T,
	): T => {
		return {
			...defaultObject,
			...currentObject,
			[key]: value,
		};
	};

	// UNIFIED: Card-specific rate update handler that updates both rate_config and rate-type-specific fields
	const updateUnifiedCardSpecificDiscountRate = (
		cardType: string,
		value: number,
		rateType?: 'ic_plus' | 'flat_tiered',
	) => {
		const updates: Partial<PricingConfiguration> = {};

		// Determine the active rate type
		const activeRateType =
			rateType || (hasRateType('FLAT_TIERED') ? 'flat_tiered' : 'ic_plus');

		// Update rate-type-specific card rates
		if (activeRateType === 'flat_tiered') {
			const currentRates =
				configuration.flat_tiered_card_specific_discount_rates || {
					visa_mastercard_discover_credit: 2.9,
					visa_mastercard_discover_debit: 2.9,
					american_express: 2.9,
					pin_debit: 2.9,
				};
			updates.flat_tiered_card_specific_discount_rates = {
				...currentRates,
				[cardType]: value,
			};
		} else {
			const currentRates =
				configuration.ic_plus_card_specific_discount_rates || {
					visa_mastercard_discover_credit: 0.65,
					visa_mastercard_discover_debit: 0.45,
					american_express: 0.7,
					pin_debit: 0.55,
				};
			updates.ic_plus_card_specific_discount_rates = {
				...currentRates,
				[cardType]: value,
			};
		}

		// Also update rate_config for backward compatibility with transformer
		const currentRateConfig = configuration.rate_config || {
			mode: 'same_for_all',
			standardRate: activeRateType === 'flat_tiered' ? 2.9 : 0.65,
			standardAuthFee: activeRateType === 'flat_tiered' ? 0.3 : 0.1,
			visa_mastercard_discover_credit: { discountRate: 2.9, authFee: 0.3 },
			visa_mastercard_discover_debit: { discountRate: 2.9, authFee: 0.3 },
			american_express: { discountRate: 2.9, authFee: 0.3 },
			pin_debit: { discountRate: 2.9, authFee: 0.3 },
		};

		const cardRate = currentRateConfig[cardType as keyof UnifiedRateConfig];
		updates.rate_config = {
			...currentRateConfig,
			[cardType as keyof UnifiedRateConfig]: {
				...(typeof cardRate === 'object' && cardRate !== null ? cardRate : {}),
				discountRate: value,
			} as any,
		};

		onUpdate(updates);
	};

	const updateUnifiedTransactionSpecificFee = (
		transactionType: string,
		value: number,
		rateType?: 'ic_plus' | 'flat_tiered',
	) => {
		const updates: Partial<PricingConfiguration> = {};

		// Determine the active rate type
		const activeRateType =
			rateType || (hasRateType('FLAT_TIERED') ? 'flat_tiered' : 'ic_plus');

		// Update rate-type-specific transaction fees
		if (activeRateType === 'flat_tiered') {
			const currentFees =
				configuration.flat_tiered_transaction_specific_fees || {
					credit_transactions: 0.3,
					debit_transactions: 0.3,
					pin_debit_transactions: 0.3,
				};
			updates.flat_tiered_transaction_specific_fees = {
				...currentFees,
				[transactionType]: value,
			};
		} else {
			const currentFees = configuration.ic_plus_transaction_specific_fees || {
				credit_transactions: 0.1,
				debit_transactions: 0.08,
				pin_debit_transactions: 0.07,
			};
			updates.ic_plus_transaction_specific_fees = {
				...currentFees,
				[transactionType]: value,
			};
		}

		// Also update rate_config for backward compatibility
		const currentRateConfig = configuration.rate_config || {
			mode: 'same_for_all',
			standardRate: activeRateType === 'flat_tiered' ? 2.9 : 0.65,
			standardAuthFee: activeRateType === 'flat_tiered' ? 0.3 : 0.1,
			visa_mastercard_discover_credit: { discountRate: 2.9, authFee: 0.3 },
			visa_mastercard_discover_debit: { discountRate: 2.9, authFee: 0.3 },
			american_express: { discountRate: 2.9, authFee: 0.3 },
			pin_debit: { discountRate: 2.9, authFee: 0.3 },
		};

		updates.rate_config = {
			...currentRateConfig,
			standardAuthFee: value,
			visa_mastercard_discover_credit: {
				...currentRateConfig.visa_mastercard_discover_credit,
				authFee: value,
			},
			visa_mastercard_discover_debit: {
				...currentRateConfig.visa_mastercard_discover_debit,
				authFee: value,
			},
			american_express: {
				...currentRateConfig.american_express,
				authFee: value,
			},
			pin_debit: { ...currentRateConfig.pin_debit, authFee: value },
		};

		onUpdate(updates);
	};

	const updateFlatTieredTransactionSpecificFee = (
		transactionType: string,
		value: number,
	) => {
		const defaultFees = {
			credit_transactions: 0.1,
			debit_transactions: 0.08,
			pin_debit_transactions: 0.07,
		};

		const updatedFees = updateNestedProperty(
			configuration.flat_tiered_transaction_specific_fees,
			transactionType as keyof typeof defaultFees,
			value,
			defaultFees,
		);

		onUpdate({ flat_tiered_transaction_specific_fees: updatedFees });
	};

	const handleSameAsAboveChange = (checked: boolean) => {
		setSameAsAbove(checked);

		// "Same as above" is specific to TIERED pricing model
		// It should use tiered_same_as_credit_6tier and NOT affect the global or IC_PLUS mode
		if (checked) {
			onUpdate({
				tiered_same_as_credit_6tier: checked,
				same_as_credit_6tier: checked,
				debit_rates: configuration.credit_rates,
			});
		} else {
			onUpdate({
				tiered_same_as_credit_6tier: checked,
				same_as_credit_6tier: checked,
				debit_rates: configuration.debit_rates || {
					qualified_rate: 1.6,
					qualified_per_item: 0.79,
					midqualified_rate: 1.7,
					midqualified_per_item: 0.79,
					nonqualified_rate: 2.2,
					nonqualified_per_item: 0.79,
				},
			});
		}
	};

	return (
		<div className="space-y-8">
			{/* Multiple Pricing Models - Show combined IC_PLUS and TIERED sections when both are selected */}
			{hasRateType('IC_PLUS') && hasRateType('TIERED') && (
				<>
					<div className="text-center mb-8">
						<h3 className="text-2xl text-gray-900 mb-2">
							Configure Both Pricing Models
						</h3>
						<p className="text-gray-600">
							Set up rates for both Interchange-Plus and Tiered pricing models
						</p>
					</div>

					<div className="rounded-lg p-6">
						<h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
							<span className="h-2 rounded-full"></span>
							Interchange-Plus Pricing
						</h4>
						<p className="text-gray-700 mb-6">
							Configure your discount rates and authorization fees for
							Interchange-Plus pricing
						</p>
						<ICPlusSection
							configuration={configuration}
							onUpdate={onUpdate}
							handleDiscountRateChange={handleDiscountRateModeChange}
							updateCardSpecificDiscountRate={
								updateUnifiedCardSpecificDiscountRate
							}
							updateTransactionSpecificFee={updateUnifiedTransactionSpecificFee}
							rateType="ic_plus"
						/>
					</div>
					<div className="h-1 bg-gray-200"></div>

					<div className="rounded-lg p-6">
						<h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
							<span className="h-2 rounded-full"></span>
							Tiered Pricing
						</h4>
						<p className="text-gray-700 mb-6">
							Configure your tiered rates for Qualified, Mid-Qualified, and
							Non-Qualified transactions
						</p>
						<TieredSection
							configuration={configuration}
							onUpdate={onUpdate}
							handleDiscountRateChange={handleTieredDiscountRateModeChange}
							threeTierRates={threeTierRates}
							sixTierRates={sixTierRates}
							sameAsAbove={sameAsAbove}
							setSameAsAbove={setSameAsAbove}
							handleSameAsAboveChange={handleSameAsAboveChange}
							updateCardSpecificDiscountRate={
								updateTieredCardSpecificDiscountRate
							}
							updateTransactionSpecificFee={updateTieredTransactionSpecificFee}
						/>
					</div>
					<div className="h-1 bg-gray-200"></div>
				</>
			)}

			{/* Single TIERED Pricing Model - only show when TIERED is selected but not IC_PLUS */}
			{hasRateType('TIERED') && !hasRateType('IC_PLUS') && (
				<>
					<div className="text-center mb-8">
						<h3 className="text-2xl text-gray-900 mb-2">
							Your Discount Rate (How You Make Money)
						</h3>
						<p className="text-gray-600">
							Define your profit margins on top of wholesale costs
						</p>
					</div>
					<div className="rounded-lg p-6">
						<h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
							<span className="h-2 rounded-full"></span>
							Tiered Pricing
						</h4>
						<p className="text-gray-700 mb-6">
							Configure your tiered rates for Qualified, Mid-Qualified, and
							Non-Qualified transactions
						</p>
						<TieredSection
							configuration={configuration}
							onUpdate={onUpdate}
							handleDiscountRateChange={handleTieredDiscountRateModeChange}
							threeTierRates={threeTierRates}
							sixTierRates={sixTierRates}
							sameAsAbove={sameAsAbove}
							setSameAsAbove={setSameAsAbove}
							handleSameAsAboveChange={handleSameAsAboveChange}
							updateCardSpecificDiscountRate={
								updateTieredCardSpecificDiscountRate
							}
							updateTransactionSpecificFee={updateTieredTransactionSpecificFee}
						/>
					</div>
				</>
			)}

			{/* Single IC_PLUS pricing model - only show when IC_PLUS is selected but not TIERED */}
			{hasRateType('IC_PLUS') && !hasRateType('TIERED') && (
				<>
					<div className="text-center mb-8">
						<h3 className="text-2xl text-gray-900 mb-2">
							Your Discount Rate (How You Make Money)
						</h3>
						<p className="text-gray-600">
							Define your profit margins on top of wholesale costs
						</p>
					</div>
					<div className="rounded-lg p-6">
						<h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
							<span className="h-2 rounded-full"></span>
							Interchange-Plus Pricing
						</h4>
						<p className="text-gray-700 mb-6">
							Configure your discount rates and authorization fees for
							Interchange-Plus pricing
						</p>
						<ICPlusSection
							configuration={configuration}
							onUpdate={onUpdate}
							handleDiscountRateChange={handleDiscountRateModeChange}
							updateCardSpecificDiscountRate={
								updateUnifiedCardSpecificDiscountRate
							}
							updateTransactionSpecificFee={updateUnifiedTransactionSpecificFee}
							rateType="ic_plus"
						/>
					</div>
				</>
			)}

			{hasRateType('SURCHARGE') && (
				<>
					<div className="text-center mb-8">
						<h3 className="text-2xl text-gray-900 mb-2">Surcharge Rates</h3>
						<p className="text-gray-600">
							The cardholder pays the surcharge. The merchant pays one rate on
							the surcharged volume.
						</p>
					</div>
					<SurchargeSection configuration={configuration} onUpdate={onUpdate} />
				</>
			)}

			{/* FLAT_TIERED pricing model - show whenever FLAT_TIERED is selected */}
			{hasRateType('FLAT_TIERED') && (
				<>
					{!hasRateType('IC_PLUS') && !hasRateType('TIERED') && (
						<div className="text-center mb-8">
							<h3 className="text-2xl text-gray-900 mb-2">
								Flat Tiered Rates Configuration
							</h3>
							<p className="text-gray-600">
								Configure flat rates per transaction for Stripe/Square-style
								statements
							</p>
						</div>
					)}
					<div className="rounded-lg p-6">
						<h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
							<span className="h-2 rounded-full"></span>
							Flat Tiered Pricing
						</h4>
						<p className="text-gray-700 mb-6">
							Configure your flat rates per transaction. This model is used for
							transaction-level statements like Stripe or Square.
						</p>
						<ICPlusSection
							configuration={configuration}
							onUpdate={onUpdate}
							handleDiscountRateChange={handleDiscountRateModeChange}
							updateCardSpecificDiscountRate={
								updateUnifiedCardSpecificDiscountRate
							}
							updateTransactionSpecificFee={updateUnifiedTransactionSpecificFee}
							rateType="flat_tiered"
						/>
					</div>
				</>
			)}
		</div>
	);
};
